import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { cn } from '../lib/classnames.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';
import { bgRegistrationScale } from '../spatial/registration.js';

/**
 * SoftMpiSculpt — POC renderer for the soft-alpha multi-plane (soft-MPI)
 * representation. It composites N back-to-front premultiplied-alpha textured
 * quads at INVERSE-DEPTH Z inside the SAME ornate gold 9-slice frame + mat +
 * camera-orbit as LivingSculpt, so it is an apples-to-apples A/B against the
 * shipping LDI / solo renderer.
 *
 * The planes are produced by tools/spatial2/softmpi.py: plane_00 is the opaque
 * inpainted background (real see-behind), planes 01..N-1 carry the subject's
 * soft matte alpha DISTRIBUTED across depth via a = 1-(1-a)^w (alpha-preserving,
 * so the interior stays opaque and the fuzzy edge becomes an opacity ramp
 * through depth -> no rim). Content is byte-faithful (NoColorSpace textures,
 * toneMapped:false, raw shader); only the gold frame gets ACES + PBR.
 *
 * Deterministic drive hooks match LivingSculpt: window.__sculptStill (rest),
 * window.__sculptPose = {sx,sy} (pinned orbit), data-sculpt-mode / __sculptDiag.
 */

const FRAME_SRC = 'https://api.gilding.app/images/assets/frame-ornate.png';
const FRAME_T = 0.27;
const FRAME_MT = 0.42;
const FRAME_OPEN_H = 2.06;
const FRAME_TRANS = 0.19;
const FRAME_OVL = 0.05;
const FRAME_OUTER = FRAME_MT * (1 - FRAME_TRANS) - FRAME_OVL;

export function frameOuterAspect(imgAspect: number): number {
  const oh = FRAME_OPEN_H;
  const ow = oh * imgAspect;
  return (ow + 2 * FRAME_OUTER) / (oh + 2 * FRAME_OUTER);
}

const CAM = 6.2;
const ORBIT_AMP_X = 0.25;
const ORBIT_AMP_Y = 0.15;
// soft-MPI plane stack world-Z span (group-local; FILL rescales -> registered
// in world space per plane). u=0 -> Z_BACK (bg, far), u=1 -> Z_FRONT (nearest).
const Z_BACK = -0.55; // == LDI bg plate
const Z_FRONT = 0.16; // a touch forward of LDI fg (0.11) for pop on the pancaked whale

const CONTENT_VERT = /* glsl */ `
  varying vec2 vUv;
  void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
// Premultiplied at upload; composited with (ONE, ONE_MINUS_SRC_ALPHA).
const CONTENT_FRAG = /* glsl */ `
  precision highp float;
  uniform sampler2D uMap;
  varying vec2 vUv;
  void main(){ gl_FragColor = texture2D(uMap, vUv); }
`;

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
}

interface SoftMpiAssets {
  planes: HTMLImageElement[]; // index 0 = bg (opaque), 1..N-1 subject
  u: number[];
}

async function loadSoftMpi(base: string): Promise<SoftMpiAssets | null> {
  try {
    const res = await fetch(`${base}/meta.json`);
    if (!res.ok) return null;
    const j = (await res.json()) as { N?: number; u?: number[]; kind?: string };
    if (j.kind !== 'softmpi' || typeof j.N !== 'number' || !Array.isArray(j.u)) return null;
    const N = j.N;
    const planes = await Promise.all(
      Array.from({ length: N }, (_v, i) => loadImg(`${base}/plane_${String(i).padStart(2, '0')}.png`)),
    );
    return { planes, u: j.u };
  } catch {
    return null;
  }
}

function contentTex(img: HTMLImageElement, renderer: THREE.WebGLRenderer, premultiply: boolean): THREE.Texture {
  const t = new THREE.Texture(img);
  t.colorSpace = THREE.NoColorSpace;
  t.anisotropy = renderer.capabilities.getMaxAnisotropy();
  // NO MIPMAPS on content planes. Mip down-filtering at the small framed
  // viewport resampled the native 1536px cover to a lower-res mip and washed
  // out the hero's micro-contrast (milky glow / dimmed headlamp) -> the rest
  // veil. LinearFilter samples the native texels directly; the planes are
  // 1536px, at or above the framed pixel density, so magnification is minimal
  // and there is no mip contrast loss.
  t.minFilter = THREE.LinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.premultiplyAlpha = premultiply;
  t.needsUpdate = true;
  return t;
}

function frameNormalTex(img: HTMLImageElement): THREE.CanvasTexture {
  const a = img.naturalWidth / img.naturalHeight;
  const DW = Math.min(256, img.naturalWidth);
  const DH = Math.max(1, Math.round(DW / a));
  const c = document.createElement('canvas');
  c.width = DW;
  c.height = DH;
  const x = c.getContext('2d');
  if (!x) throw new Error('no 2d ctx');
  x.drawImage(img, 0, 0, DW, DH);
  const raw = x.getImageData(0, 0, DW, DH).data;
  const g = new Float32Array(DW * DH);
  for (let i = 0; i < DW * DH; i += 1) {
    g[i] = (0.299 * raw[i * 4]! + 0.587 * raw[i * 4 + 1]! + 0.114 * raw[i * 4 + 2]!) / 255;
  }
  const nC = document.createElement('canvas');
  nC.width = DW;
  nC.height = DH;
  const nctx = nC.getContext('2d')!;
  const nI = nctx.createImageData(DW, DH);
  const str = 3.0;
  for (let y = 0; y < DH; y += 1)
    for (let xx = 0; xx < DW; xx += 1) {
      const gx = (g[y * DW + Math.min(DW - 1, xx + 1)]! - g[y * DW + Math.max(0, xx - 1)]!) * str;
      const gy = (g[Math.min(DH - 1, y + 1) * DW + xx]! - g[Math.max(0, y - 1) * DW + xx]!) * str;
      let ax = -gx;
      let ay = gy;
      let az = 1;
      const l = Math.hypot(ax, ay, az);
      ax /= l;
      ay /= l;
      az /= l;
      const o = (y * DW + xx) * 4;
      nI.data[o] = (ax * 0.5 + 0.5) * 255;
      nI.data[o + 1] = (ay * 0.5 + 0.5) * 255;
      nI.data[o + 2] = (az * 0.5 + 0.5) * 255;
      nI.data[o + 3] = 255;
    }
  nctx.putImageData(nI, 0, 0);
  return new THREE.CanvasTexture(nC);
}

interface SoftMpiApi {
  dispose: () => void;
}

function buildScene(
  holder: HTMLDivElement,
  art: HTMLImageElement,
  frm: HTMLImageElement,
  assets: SoftMpiAssets,
  ambient: boolean,
): SoftMpiApi {
  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
  const r0 = holder.getBoundingClientRect();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(r0.width, r0.height);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  renderer.domElement.style.display = 'block';
  holder.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const cam = new THREE.PerspectiveCamera(28, r0.width / r0.height, 0.1, 30);
  cam.position.set(0, 0, CAM);
  cam.lookAt(0, 0, 0);

  const visibleH = 2 * CAM * Math.tan((28 * Math.PI) / 180 / 2);
  const aImg = art.naturalWidth / art.naturalHeight;
  const longAx = Math.max(aImg, 1 / aImg);
  const aspectDeviation = Math.min(1, (longAx - 1) / 0.78);
  const ORBIT_SCALE = 1 / (0.5 + 0.5 * longAx);
  const FILL_OVERSCAN = 0.96 - 0.08 * aspectDeviation;
  const FILL = (visibleH / (FRAME_OPEN_H + 2 * FRAME_OUTER)) * FILL_OVERSCAN;

  // studio env for the gold frame
  const ec = document.createElement('canvas');
  ec.width = 1024;
  ec.height = 512;
  const ex = ec.getContext('2d')!;
  const gr = ex.createLinearGradient(0, 0, 0, 512);
  gr.addColorStop(0, '#2b2535');
  gr.addColorStop(0.42, '#1a1622');
  gr.addColorStop(0.5, '#3d3344');
  gr.addColorStop(1, '#070509');
  ex.fillStyle = gr;
  ex.fillRect(0, 0, 1024, 512);
  const rg = ex.createRadialGradient(360, 150, 0, 360, 150, 260);
  rg.addColorStop(0, '#fff3d6');
  rg.addColorStop(0.5, 'rgba(255,225,170,.5)');
  rg.addColorStop(1, 'rgba(0,0,0,0)');
  ex.fillStyle = rg;
  ex.fillRect(0, 0, 1024, 512);
  const envTex = new THREE.CanvasTexture(ec);
  envTex.mapping = THREE.EquirectangularReflectionMapping;
  envTex.colorSpace = THREE.SRGBColorSpace;
  scene.environment = envTex;

  const pivot = new THREE.Group();
  scene.add(pivot);
  const group = new THREE.Group();
  pivot.add(group);
  group.scale.setScalar(FILL);

  const oh = FRAME_OPEN_H;
  const ow = oh * aImg;

  const disposables: { dispose: () => void }[] = [];
  const diag: Record<string, unknown> = { mode: 'softmpi', N: assets.planes.length, fill: FILL };

  // ── N soft-MPI planes at inverse-depth Z, registered to the opening ────────
  const N = assets.planes.length;
  const xiBack = 1 / (CAM - Z_BACK);
  const xiFront = 1 / (CAM - Z_FRONT);
  scene.updateMatrixWorld(true);
  const openW = group.getWorldPosition(new THREE.Vector3());
  const zLocal: number[] = [];
  for (let j = 0; j < N; j += 1) {
    const u = assets.u[j] ?? j / (N - 1);
    const xi = xiBack + u * (xiFront - xiBack); // inverse-depth (disparity) linear in u
    const z = CAM - 1 / xi; // world-equivalent local z (group is centred at origin)
    zLocal.push(z);
    const tex = contentTex(assets.planes[j]!, renderer, true);
    disposables.push(tex);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uMap: { value: tex } },
      vertexShader: CONTENT_VERT,
      fragmentShader: CONTENT_FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      toneMapped: false,
    });
    mat.blending = THREE.CustomBlending;
    mat.blendSrc = THREE.OneFactor;
    mat.blendDst = THREE.OneMinusSrcAlphaFactor;
    mat.blendSrcAlpha = THREE.OneFactor;
    mat.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(ow, oh, 1, 1), mat);
    mesh.position.set(0, 0, z);
    mesh.renderOrder = j; // back (bg, j=0) -> front
    group.add(mesh);
    scene.updateMatrixWorld(true);
    const pw = mesh.getWorldPosition(new THREE.Vector3());
    // PRE-BAKED per-plane registration: k is computed ONCE here at scene-build
    // and stored as a static mesh.scale — it is NEVER recomputed per frame, so
    // the front subject planes are not rescaled at runtime (only the camera
    // orbits). Combined with the builder's opaque-interior collapse (the hero's
    // opaque body now lives on a SINGLE plane, not 2-3), there are no redundant
    // opaque copies at different Z/k to mis-occlude -> the "per-Z registration
    // rescale" veil cause is eliminated by construction.
    const k = bgRegistrationScale(cam.position.z, openW.z, pw.z);
    mesh.scale.set(k, k, 1);
  }
  Object.assign(diag, { zLocal, xiBack, xiFront });

  // ── OPAQUE MAT RING (identical geometry to LivingSculpt) ───────────────────
  {
    const lip = 0.03;
    const MAT_OUT = 0.15;
    const inW = ow / 2 - lip;
    const inH = oh / 2 - lip;
    const outW = ow / 2 + MAT_OUT;
    const outH = oh / 2 + MAT_OUT;
    const matMat = new THREE.MeshBasicMaterial({ color: 0x0a0810 });
    const addMat = (w: number, h: number, x: number, y: number): void => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), matMat);
      m.position.set(x, y, Z_FRONT + 0.02);
      m.renderOrder = N + 1;
      group.add(m);
    };
    addMat(2 * outW, outH - inH, 0, (inH + outH) / 2);
    addMat(2 * outW, outH - inH, 0, -(inH + outH) / 2);
    addMat(outW - inW, 2 * inH, -(inW + outW) / 2, 0);
    addMat(outW - inW, 2 * inH, (inW + outW) / 2, 0);
  }

  // ── 9-SLICE ornate gold frame ──────────────────────────────────────────────
  const fTex = new THREE.Texture(frm);
  fTex.colorSpace = THREE.SRGBColorSpace;
  fTex.anisotropy = renderer.capabilities.getMaxAnisotropy();
  fTex.needsUpdate = true;
  const fNorm = frameNormalTex(frm);
  disposables.push(fTex, fNorm);
  const fMat = new THREE.MeshStandardMaterial({
    map: fTex,
    transparent: true,
    alphaTest: 0.5,
    normalMap: fNorm,
    normalScale: new THREE.Vector2(0.8, 0.8),
    metalness: 1,
    roughness: 0.3,
    envMapIntensity: 1.35,
  });
  const t = FRAME_T;
  const mt = FRAME_MT;
  const exL = ow / 2 - FRAME_OVL + mt * (0.5 - FRAME_TRANS);
  const eyT = oh / 2 - FRAME_OVL + mt * (0.5 - FRAME_TRANS);
  const iw = 2 * exL - mt;
  const ih = 2 * eyT - mt;
  const zFrame = Z_FRONT + 0.04;
  const framePiece = (w: number, h: number, cx: number, cy: number, u0: number, v0: number, u1: number, v1: number): void => {
    const g = new THREE.PlaneGeometry(w, h, 1, 1);
    const uv = g.attributes.uv as THREE.BufferAttribute;
    uv.setXY(0, u0, v1);
    uv.setXY(1, u1, v1);
    uv.setXY(2, u0, v0);
    uv.setXY(3, u1, v0);
    uv.needsUpdate = true;
    const m = new THREE.Mesh(g, fMat);
    m.position.set(cx, cy, zFrame);
    m.renderOrder = N + 5;
    group.add(m);
  };
  framePiece(mt, mt, -exL, +eyT, 0, 1 - t, t, 1);
  framePiece(mt, mt, +exL, +eyT, 1 - t, 1 - t, 1, 1);
  framePiece(mt, mt, -exL, -eyT, 0, 0, t, t);
  framePiece(mt, mt, +exL, -eyT, 1 - t, 0, 1, t);
  const NATURAL = (mt * (1 - 2 * t)) / t;
  const oddRep = (len: number): number => {
    const x = len / NATURAL;
    let r = Math.max(1, Math.round(x));
    if (r % 2 === 0) r = (x > r ? r + 1 : r - 1) || 1;
    return Math.max(1, r);
  };
  const frameEdge = (horizontal: boolean, length: number, cx: number, cy: number, sa0: number, sa1: number, sb0: number, sb1: number, rep: number): void => {
    const g = horizontal ? new THREE.PlaneGeometry(length, mt, rep, 1) : new THREE.PlaneGeometry(mt, length, 1, rep);
    const uv = g.attributes.uv as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i += 1) {
      const gu = uv.getX(i);
      const gv = uv.getY(i);
      const f = horizontal ? gu : gv;
      const c = horizontal ? gv : gu;
      const b = Math.round(f * rep);
      const aa = b % 2 === 0 ? sa0 : sa1;
      const bb = sb0 + c * (sb1 - sb0);
      if (horizontal) uv.setXY(i, aa, bb);
      else uv.setXY(i, bb, aa);
    }
    uv.needsUpdate = true;
    const m = new THREE.Mesh(g, fMat);
    m.position.set(cx, cy, zFrame);
    m.renderOrder = N + 5;
    group.add(m);
  };
  const repH = oddRep(iw);
  const repV = oddRep(ih);
  frameEdge(true, iw, 0, +eyT, t, 1 - t, 1 - t, 1, repH);
  frameEdge(true, iw, 0, -eyT, t, 1 - t, 0, t, repH);
  frameEdge(false, ih, -exL, 0, t, 1 - t, 0, t, repV);
  frameEdge(false, ih, +exL, 0, t, 1 - t, 1 - t, 1, repV);

  scene.add(new THREE.AmbientLight(0xfff0d8, 0.34));
  const key = new THREE.DirectionalLight(0xffe6bd, 2.3);
  key.position.set(-1.6, 1.8, 2.4);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x9fb4ff, 0.85);
  rim.position.set(2.0, -0.7, 1.2);
  scene.add(rim);

  holder.dataset.sculptMode = 'softmpi';
  if (typeof window !== 'undefined') {
    (window as unknown as { __sculptDiag?: unknown }).__sculptDiag = diag;
  }

  const KEY_SWING = 0.95;
  const applyPose = (sx: number, sy: number): void => {
    cam.position.set(ORBIT_AMP_X * sx, ORBIT_AMP_Y * sy, CAM);
    cam.lookAt(0, 0, 0);
    key.position.set(-1.6 + sx * KEY_SWING, 1.8 - sy * KEY_SWING, 2.4);
  };

  let tx = 0;
  let ty = 0;
  let rxv = 0;
  let ryv = 0;
  let active = false;
  let idle = 0;
  const ev = (cx: number, cy: number): void => {
    const r = holder.getBoundingClientRect();
    tx = Math.max(-1, Math.min(1, ((cx - r.left) / r.width) * 2 - 1));
    ty = Math.max(-1, Math.min(1, ((cy - r.top) / r.height) * 2 - 1));
    active = true;
  };
  const onPointer = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') ev(e.clientX, e.clientY);
  };
  const onLeave = (): void => {
    active = false;
  };
  holder.addEventListener('pointermove', onPointer);
  holder.addEventListener('pointerleave', onLeave);

  const onResize = (): void => {
    const r = holder.getBoundingClientRect();
    if (!r.width || !r.height) return;
    cam.aspect = r.width / r.height;
    cam.updateProjectionMatrix();
    renderer.setSize(r.width, r.height);
  };
  window.addEventListener('resize', onResize);
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
  ro?.observe(holder);

  const clock = new THREE.Clock();
  let raf = 0;
  let ambElapsed = 0;
  const loop = (): void => {
    raf = requestAnimationFrame(loop);
    const dt = Math.min(0.05, clock.getDelta());
    if ((window as unknown as { __sculptStill?: boolean }).__sculptStill === true) {
      applyPose(0, 0);
      renderer.render(scene, cam);
      return;
    }
    const pinned = (window as unknown as { __sculptPose?: { sx: number; sy: number } }).__sculptPose;
    if (pinned && Number.isFinite(pinned.sx) && Number.isFinite(pinned.sy)) {
      applyPose(Math.max(-1, Math.min(1, pinned.sx)), Math.max(-1, Math.min(1, pinned.sy)));
      renderer.render(scene, cam);
      return;
    }
    if (ambient) {
      ambElapsed += dt;
      const ph = (ambElapsed / 6.2) * Math.PI * 2;
      applyPose(ORBIT_SCALE * Math.cos(ph), ORBIT_SCALE * Math.sin(ph));
      renderer.render(scene, cam);
      return;
    }
    let gx = tx;
    let gy = ty;
    if (!active) {
      idle += dt;
      gx = Math.sin(idle * 0.45) * 0.32;
      gy = Math.cos(idle * 0.38) * 0.22;
    }
    ryv += (gx - ryv) * 0.12;
    rxv += (-gy - rxv) * 0.12;
    applyPose(ryv, rxv);
    renderer.render(scene, cam);
  };
  raf = requestAnimationFrame(loop);

  const dispose = (): void => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', onResize);
    ro?.disconnect();
    holder.removeEventListener('pointermove', onPointer);
    holder.removeEventListener('pointerleave', onLeave);
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else if (mat) mat.dispose();
    });
    envTex.dispose();
    for (const d of disposables) d.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
  };

  return { dispose };
}

export function SoftMpiSculpt({
  src,
  base,
  aspect = 1,
  ambient = true,
  className,
  heightCap = '86svh',
}: {
  src: string;
  base: string;
  aspect?: number;
  ambient?: boolean;
  className?: string;
  heightCap?: string;
}) {
  const reduced = useReducedMotion();
  const holderRef = useRef<HTMLDivElement>(null);
  const [fallback, setFallback] = useState(false);
  const frameAspect = frameOuterAspect(aspect);

  useEffect(() => {
    if (reduced) {
      setFallback(true);
      return undefined;
    }
    const holder = holderRef.current;
    if (!holder) return undefined;
    let disposed = false;
    let api: SoftMpiApi | null = null;
    void (async () => {
      try {
        const [art, frm, assets] = await Promise.all([loadImg(src), loadImg(FRAME_SRC), loadSoftMpi(base)]);
        if (disposed) return;
        if (!assets) {
          setFallback(true);
          return;
        }
        api = buildScene(holder, art, frm, assets, ambient);
      } catch (e) {
        console.warn('SoftMpiSculpt failed', e);
        setFallback(true);
      }
    })();
    return () => {
      disposed = true;
      api?.dispose();
    };
  }, [reduced, src, base, ambient]);

  const fitWidth = `min(92vw, calc(${heightCap} * ${frameAspect}))`;
  if (reduced || fallback) {
    return <img src={src} alt="" className={cn('block', className)} style={{ width: `min(92vw, calc(${heightCap} * ${aspect}))` }} />;
  }
  return (
    <div className={cn('relative select-none', className)} style={{ aspectRatio: String(frameAspect), width: fitWidth }}>
      <div ref={holderRef} className="absolute inset-0 touch-none" />
    </div>
  );
}
